import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Package, 
  Route, 
  DoorOpen,
  ArrowLeft,
  Plus,
  Minus,
  Snowflake,
  Sun,
  Search,
  AlertTriangle,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TireMapView = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [tiles, setTiles] = useState([]);
  const [storedTires, setStoredTires] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTile, setSelectedTile] = useState(null);
  const [storeDialog, setStoreDialog] = useState(false);
  const [searchPlate, setSearchPlate] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Store tire form
  const [storeForm, setStoreForm] = useState({
    size: "",
    brand: "",
    profile_depths: ["", "", "", "", "", ""],  // Array for individual depths (max 6)
    quantity: 4,
    license_plate: "",
    is_winter: false
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load tiles
      const tilesRes = await axios.get(`${API}/tiremap/tiles`);
      setTiles(tilesRes.data.filter(t => t.tile_type)); // Only show configured tiles

      // Load stored tires and calculate usage per tile
      const tiresRes = await axios.get(`${API}/tiremap/tires`);
      const tiresMap = {};
      const usageMap = {};
      
      tiresRes.data.forEach(tire => {
        if (!tiresMap[tire.tile_id]) {
          tiresMap[tire.tile_id] = [];
          usageMap[tire.tile_id] = 0;
        }
        tiresMap[tire.tile_id].push(tire);
        usageMap[tire.tile_id] += tire.quantity || 1;
      });
      
      setStoredTires(tiresMap);
      setTileUsage(usageMap);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  // Track usage per tile
  const [tileUsage, setTileUsage] = useState({});

  // Handle tile click
  const handleTileClick = (tile) => {
    if (tile.tile_type !== "storage") return;
    setSelectedTile(tile);
    setStoreForm({
      size: "",
      brand: "",
      profile_depths: ["", "", "", "", "", ""],
      quantity: 4,
      license_plate: "",
      is_winter: false
    });
    setStoreDialog(true);
  };

  // Update quantity and resize profile_depths array
  const updateQuantity = (newQty) => {
    const qty = Math.max(1, Math.min(newQty, 20));
    setStoreForm(prev => {
      const newDepths = [...prev.profile_depths];
      while (newDepths.length < qty) newDepths.push("");
      while (newDepths.length > qty) newDepths.pop();
      return { ...prev, quantity: qty, profile_depths: newDepths };
    });
  };

  // Store tire set
  const storeTire = async () => {
    if (!selectedTile) return;

    // Validate
    if (!storeForm.size || !storeForm.brand || !storeForm.license_plate) {
      toast.error("Bitte alle Felder ausfüllen");
      return;
    }

    // Validate all profile depths
    const depths = storeForm.profile_depths.slice(0, storeForm.quantity);
    if (depths.some(d => !d || isNaN(parseFloat(d)))) {
      toast.error("Bitte alle Profiltiefen angeben");
      return;
    }

    const quantity = storeForm.quantity;
    
    // Check capacity
    const currentUsage = tileUsage[selectedTile.id] || 0;
    const availableSpace = selectedTile.capacity - currentUsage;
    
    if (quantity > availableSpace) {
      toast.error(`Nicht genug Platz! Verfügbar: ${availableSpace} von ${selectedTile.capacity}`);
      return;
    }

    try {
      await axios.post(`${API}/tiremap/tires`, {
        tile_id: selectedTile.id,
        size: storeForm.size,
        brand: storeForm.brand,
        profile_depths: depths.map(d => parseFloat(d)),
        quantity: quantity,
        license_plate: storeForm.license_plate.toUpperCase(),
        is_winter: storeForm.is_winter
      }, authHeaders);

      toast.success(`${quantity} Reifen eingelagert`);
      loadData();
      setStoreForm({
        size: "",
        brand: "",
        profile_depths: ["", "", "", ""],
        quantity: 4,
        license_plate: "",
        is_winter: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Einlagern");
    }
  };

  // Remove tire
  const removeTire = async (tireId) => {
    try {
      await axios.delete(`${API}/tiremap/tires/${tireId}`, authHeaders);
      toast.success("Reifen ausgelagert");
      loadData();
    } catch (error) {
      toast.error("Fehler beim Auslagern");
    }
  };

  // Search by license plate
  const searchByPlate = async () => {
    if (!searchPlate) return;
    try {
      const res = await axios.get(`${API}/tiremap/tires/by-plate/${searchPlate}`);
      setSearchResults(res.data);
      if (res.data.length === 0) {
        toast.info("Keine Reifen mit diesem Kennzeichen gefunden");
      }
    } catch (error) {
      toast.error("Fehler bei der Suche");
    }
  };

  // Get tile color and info
  const getTileStyle = (tile) => {
    const usage = tileUsage[tile.id] || 0;
    const isFull = usage >= tile.capacity;
    
    switch (tile.tile_type) {
      case "storage":
        return {
          bg: isFull ? "bg-red-100 border-red-400" : usage > 0 ? "bg-green-100 border-green-400" : "bg-green-50 border-green-300",
          text: isFull ? "text-red-700" : "text-green-700"
        };
      case "path":
        return { bg: "bg-gray-200 border-gray-400", text: "text-gray-600" };
      case "entrance":
        return { bg: "bg-blue-100 border-blue-400", text: "text-blue-700" };
      default:
        return { bg: "bg-white", text: "text-gray-600" };
    }
  };

  // Calculate grid bounds
  const getGridBounds = () => {
    if (tiles.length === 0) return { minX: 0, maxX: 5, minY: 0, maxY: 5 };
    const xs = tiles.map(t => t.x);
    const ys = tiles.map(t => t.y);
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    };
  };

  const bounds = getGridBounds();
  const gridWidth = bounds.maxX - bounds.minX + 1;
  const gridHeight = bounds.maxY - bounds.minY + 1;

  // Create tile map for quick lookup
  const tileMap = {};
  tiles.forEach(t => {
    tileMap[`${t.x}-${t.y}`] = t;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#EFDF00] border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-black text-white p-4 border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6 text-[#EFDF00]" />
              <h1 className="text-xl font-bold">Reifenlager</h1>
            </div>
          </div>
          
          {/* Search */}
          <div className="flex items-center gap-2">
            <Input
              placeholder="Kennzeichen suchen..."
              value={searchPlate}
              onChange={(e) => setSearchPlate(e.target.value.toUpperCase())}
              className="w-48 bg-gray-800 border-gray-700 text-white"
              onKeyPress={(e) => e.key === "Enter" && searchByPlate()}
            />
            <Button 
              variant="outline" 
              className="border-[#EFDF00] text-[#EFDF00]"
              onClick={searchByPlate}
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="max-w-7xl mx-auto p-4">
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-white font-bold mb-3">Suchergebnisse für "{searchPlate}"</h3>
            <div className="grid gap-2">
              {searchResults.map(tire => {
                const tile = tiles.find(t => t.id === tire.tile_id);
                return (
                  <div key={tire.id} className="bg-gray-700 p-3 rounded">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-white">
                        {tire.is_winter ? (
                          <Snowflake className="w-5 h-5 text-blue-400" />
                        ) : (
                          <Sun className="w-5 h-5 text-yellow-400" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold">{tire.license_plate}</p>
                            <Badge className="bg-[#EFDF00] text-black">{tire.quantity || 1}x</Badge>
                          </div>
                          <p className="text-sm text-gray-400">{tire.size} - {tire.brand}</p>
                        </div>
                        <Badge className="bg-green-600">
                          Lagerort: {tile?.name || "Unbekannt"}
                        </Badge>
                      </div>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => removeTire(tire.id)}
                      >
                        <Minus className="w-4 h-4 mr-1" />
                        Auslagern
                      </Button>
                    </div>
                    {tire.profile_depths && tire.profile_depths.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1 ml-9">
                        {tire.profile_depths.map((depth, idx) => (
                          <span key={idx} className="text-xs bg-gray-600 text-gray-200 px-2 py-0.5 rounded">
                            R{idx + 1}: {depth}mm
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button 
              variant="ghost" 
              className="mt-2 text-gray-400"
              onClick={() => setSearchResults([])}
            >
              Schließen
            </Button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-gray-800 p-3 mb-4 flex items-center gap-6 text-sm text-white rounded">
          <span className="font-bold">Legende:</span>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-green-50 border-2 border-green-300 rounded"></div>
            <span>Leer</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-green-100 border-2 border-green-400 rounded"></div>
            <span>Belegt</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-red-100 border-2 border-red-400 rounded"></div>
            <span>Voll</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-gray-200 border-2 border-gray-400 rounded"></div>
            <span>Gang</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-blue-100 border-2 border-blue-400 rounded"></div>
            <span>Ein-/Ausgang</span>
          </div>
        </div>

        {/* Tire Map Grid */}
        {tiles.length === 0 ? (
          <div className="bg-gray-800 p-12 text-center rounded-lg">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Kein Lager konfiguriert</p>
            <p className="text-gray-500 text-sm mt-2">
              Bitte zuerst das Lager im Editor einrichten (Login: tiremap_edit)
            </p>
          </div>
        ) : (
          <div className="bg-gray-800 p-6 rounded-lg overflow-auto">
            <div 
              className="grid gap-2 mx-auto"
              style={{
                gridTemplateColumns: `repeat(${gridWidth}, minmax(80px, 100px))`,
                width: 'fit-content'
              }}
            >
              {Array.from({ length: gridHeight }).map((_, rowIndex) =>
                Array.from({ length: gridWidth }).map((_, colIndex) => {
                  const x = bounds.minX + colIndex;
                  const y = bounds.minY + rowIndex;
                  const key = `${x}-${y}`;
                  const tile = tileMap[key];
                  
                  if (!tile) {
                    return <div key={key} className="aspect-square"></div>;
                  }

                  const style = getTileStyle(tile);
                  const usage = tileUsage[tile.id] || 0;

                  return (
                    <div
                      key={key}
                      className={`
                        aspect-square border-2 rounded-lg transition-all
                        flex flex-col items-center justify-center p-2
                        ${style.bg}
                        ${tile.tile_type === "storage" ? "cursor-pointer hover:scale-105 hover:shadow-lg" : ""}
                      `}
                      onClick={() => handleTileClick(tile)}
                    >
                      {tile.tile_type === "storage" && (
                        <>
                          <Package className={`w-5 h-5 ${style.text}`} />
                          <span className={`text-xs font-bold mt-1 ${style.text}`}>
                            {tile.name}
                          </span>
                          <span className={`text-xs ${style.text}`}>
                            {usage}/{tile.capacity}
                          </span>
                        </>
                      )}
                      {tile.tile_type === "path" && (
                        <Route className="w-5 h-5 text-gray-400" />
                      )}
                      {tile.tile_type === "entrance" && (
                        <>
                          <DoorOpen className="w-5 h-5 text-blue-600" />
                          <span className="text-xs font-bold text-blue-600 mt-1">E/A</span>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Store/View Dialog */}
      <Dialog open={storeDialog} onOpenChange={setStoreDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              {selectedTile?.name} - Reifenlager
            </DialogTitle>
          </DialogHeader>
          
          {selectedTile && (
            <div className="space-y-4">
              {/* Current tires */}
              <div>
                <Label className="text-sm font-bold">
                  Gelagerte Reifen ({tileUsage[selectedTile.id] || 0}/{selectedTile.capacity})
                </Label>
                {(tileUsage[selectedTile.id] || 0) >= selectedTile.capacity && (
                  <div className="flex items-center gap-2 text-red-600 text-sm mt-1">
                    <AlertTriangle className="w-4 h-4" />
                    Lagerplatz voll!
                  </div>
                )}
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {storedTires[selectedTile.id]?.map(tire => (
                    <div key={tire.id} className="bg-gray-100 p-3 rounded">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {tire.is_winter ? (
                            <Snowflake className="w-4 h-4 text-blue-500" />
                          ) : (
                            <Sun className="w-4 h-4 text-yellow-500" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-sm">{tire.license_plate}</p>
                              <Badge className="bg-[#EFDF00] text-black text-xs">{tire.quantity || 1}x</Badge>
                            </div>
                            <p className="text-xs text-gray-500">{tire.size} - {tire.brand}</p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => removeTire(tire.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      {/* Individual tread depths */}
                      {tire.profile_depths && tire.profile_depths.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {tire.profile_depths.map((depth, idx) => (
                            <span key={idx} className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                              R{idx + 1}: {depth}mm
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )) || (
                    <p className="text-gray-500 text-sm">Keine Reifen gelagert</p>
                  )}
                </div>
              </div>

              {/* Add new tire */}
              {(tileUsage[selectedTile.id] || 0) < selectedTile.capacity && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-bold">Reifen einlagern</Label>
                  
                  {/* Quantity Selection */}
                  <div className="mt-3 mb-4">
                    <Label className="text-xs text-gray-600 mb-2 block">Anzahl Reifen</Label>
                    <div className="flex gap-2">
                      {[1, 2, 4, 6].map(num => {
                        const currentUsage = tileUsage[selectedTile.id] || 0;
                        const available = selectedTile.capacity - currentUsage;
                        const disabled = num > available;
                        return (
                          <Button
                            key={num}
                            type="button"
                            variant={storeForm.quantity === num ? "default" : "outline"}
                            className={`flex-1 ${storeForm.quantity === num ? 'bg-[#EFDF00] text-black hover:bg-[#D9C900]' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => !disabled && updateQuantity(num)}
                            disabled={disabled}
                          >
                            {num}x
                            {num === 4 && <span className="ml-1 text-xs">(Satz)</span>}
                          </Button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Verfügbar: {selectedTile.capacity - (tileUsage[selectedTile.id] || 0)} Plätze
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Kennzeichen*</Label>
                      <Input
                        value={storeForm.license_plate}
                        onChange={(e) => setStoreForm(p => ({ ...p, license_plate: e.target.value.toUpperCase() }))}
                        placeholder="B-AB 1234"
                        className="uppercase"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Reifengröße*</Label>
                      <Input
                        value={storeForm.size}
                        onChange={(e) => setStoreForm(p => ({ ...p, size: e.target.value }))}
                        placeholder="205/55 R16"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Marke/Modell*</Label>
                      <Input
                        value={storeForm.brand}
                        onChange={(e) => setStoreForm(p => ({ ...p, brand: e.target.value }))}
                        placeholder="Continental WinterContact"
                      />
                    </div>
                  </div>
                  
                  {/* Dynamic tread depth inputs */}
                  <div className="mt-3">
                    <Label className="text-xs text-gray-600 mb-2 block">Profiltiefe je Reifen (mm)*</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {storeForm.profile_depths.slice(0, storeForm.quantity).map((depth, idx) => (
                        <div key={idx}>
                          <Label className="text-xs text-gray-400">R{idx + 1}</Label>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="10"
                            value={depth}
                            onChange={(e) => {
                              const newDepths = [...storeForm.profile_depths];
                              newDepths[idx] = e.target.value;
                              setStoreForm(p => ({ ...p, profile_depths: newDepths }));
                            }}
                            placeholder="6.5"
                            className="text-center"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Checkbox
                      checked={storeForm.is_winter}
                      onCheckedChange={(c) => setStoreForm(p => ({ ...p, is_winter: c }))}
                    />
                    <Label className="flex items-center gap-1 cursor-pointer">
                      <Snowflake className="w-4 h-4 text-blue-500" />
                      Winterreifen
                    </Label>
                  </div>
                  <Button 
                    className="w-full mt-3 bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold"
                    onClick={storeTire}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {storeForm.quantity || 1} Reifen einlagern
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TireMapView;
